import {
  useMutation,
  useQuery,
  useQueryClient,
} from "@tanstack/react-query";

import { api } from "./client";
import { queryKeys } from "./keys";
import type {
  ListResponse,
  RefillSession,
  ReservoirState,
  StartRefillInput,
  UpdateRefillInput,
} from "./types";

const base = "/v1/refill-sessions";

/** The most recent reservoir reading a session captured: post-pour if
 *  recorded, else pre-pour, else the pre-refill snapshot. */
export function latestReservoirState(
  s: RefillSession | undefined,
): ReservoirState | undefined {
  return s?.post_pour_state ?? s?.pre_pour_state ?? s?.pre_state;
}

export interface ListRefillSessionsParams {
  grow_id?: string;
  include_voided?: boolean;
  limit?: number;
  offset?: number;
}

export function useRefillSessions(params: ListRefillSessionsParams = {}) {
  return useQuery({
    queryKey: queryKeys.refillSessions.list(
      params as Record<string, unknown>,
    ),
    queryFn: ({ signal }) =>
      api.get<ListResponse<RefillSession>>(
        base,
        {
          grow_id: params.grow_id,
          include_voided: params.include_voided ? "1" : undefined,
          limit: params.limit,
          offset: params.offset,
        },
        signal,
      ),
  });
}

export function useRefillSession(id: string | undefined) {
  return useQuery({
    queryKey: id
      ? queryKeys.refillSessions.detail(id)
      : queryKeys.refillSessions.all,
    queryFn: ({ signal }) =>
      api.get<RefillSession>(`${base}/${id}`, undefined, signal),
    enabled: !!id,
  });
}

export function useStartRefillSession() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: StartRefillInput) =>
      api.post<RefillSession>(base, input),
    onSuccess: (created) => {
      qc.invalidateQueries({ queryKey: queryKeys.refillSessions.all });
      qc.setQueryData(queryKeys.refillSessions.detail(created.id), created);
    },
  });
}

export function useUpdateRefillSession(id: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: UpdateRefillInput) =>
      api.patch<RefillSession>(`${base}/${id}`, input),
    onSuccess: (updated) => {
      qc.setQueryData(queryKeys.refillSessions.detail(id), updated);
      qc.invalidateQueries({ queryKey: queryKeys.refillSessions.all });
    },
  });
}

export function useCloseRefillSession(id: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: () => api.post<void>(`${base}/${id}:close`),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: queryKeys.refillSessions.all });
    },
  });
}

export function useVoidRefillSession(id: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (reason: string) =>
      api.delete<void>(`${base}/${id}`, { reason }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: queryKeys.refillSessions.all });
    },
  });
}
